<template>
  <div class="view-wrapper search-view">
    <ViewHeader :title="$store.getters.currentPlan.name">
      <template>
        <form @submit.prevent="submitSearchInput">
          <div class="p-d-flex" style="gap: 2rem;">
            <div class="p-field">
              <label for="search">Tactic Keyword</label>
              <span class="p-input-icon-right">
                <i class="pi pi-search" @click="submitSearchInput" />
                <InputText id="search" type="text" placeholder="Search" v-model="searchInput" autocomplete="off" />
              </span>
            </div>
            <DateRangePicker :isRangeView="true" />
            <div class="p-field" style="align-self: flex-end; margin-bottom: 0.2rem;">
              <Button label="Search" icon="pi pi-search" type="submit" />
            </div>
          </div>
        </form>
      </template>
    </ViewHeader>
    <ViewMain>
      <DataTable :value="filteredResults" v-if="filteredResults.length" sortField="startDate" :sortOrder="1">
        <Column field="title" header="Topic" sortable>
          <template #body="slotProps">
            <span :class="{
              'is-nested': slotProps.data.isNested,
              'is-lead': slotProps.data.isLead,
            }">
              {{ getTitleForTactic(slotProps.data) }}
            </span>
          </template>
        </Column>
        <Column field="channelName" header="Channel" sortable />
        <Column field="tacticTypeName" header="Tactic Type" sortable />
        <Column header="Platforms" sortable>
          <template #body="slotProps">
            {{ getPlatformNameForTactic(slotProps.data) }}
          </template>
        </Column>
        <Column field="startDate" header="Start Date" sortable>
          <template #body="slotProps">{{
            slotProps.data.startDate.toLocaleDateString()
            }}</template>
        </Column>
        <Column field="endDate" header="End Date" sortable>
          <template #body="slotProps">{{
            slotProps.data.endDate.toLocaleDateString()
            }}</template>
        </Column>
        <Column>
          <template #body="slotProps">
            <div class="p-d-flex p-jc-end" v-if="
              !slotProps.data.isInitiative &&
              ((isLeadPlan && slotProps.data.isLead) ||
                (!isLeadPlan && !slotProps.data.isLead))
            ">
              <Button type="button" label="Open" icon="pi pi-arrow-circle-up" class="p-button-text"
                @click="showTactic(slotProps.data)"></Button>
            </div>
          </template>
        </Column>
      </DataTable>
      <template v-else>
        <template v-if="isLoadingResults">
          <h2>Searching tactics...</h2>
        </template>
        <template v-else-if="isEmptyKeywordSearch">
          <h2>Please enter a keyword.</h2>
        </template>
        <template v-else-if="searchQuery && searchQuery != ''">
          <h2>No results.</h2>
        </template>
      </template>
    </ViewMain>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tactic from '@/models/Tactic'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import Button from 'primevue/button'
import TacticDisplayMixin from '@/mixins/TacticDisplayMixin'
import DateRangePicker from '@/components/DateRangePicker.vue'

Vue.component('DataTable', DataTable)
Vue.component('Column', Column)
Vue.component('Button', Button)

export default Vue.extend({
  name: 'Search',
  mixins: [TacticDisplayMixin],
  components: {
    ViewHeader,
    ViewMain,
    DateRangePicker,
  },
  props: {
    searchQuery: String,
  },
  data: () => {
    return {
      allSearchResultTactics: [] as Tactic[],
      isLoadingResults: false as boolean,
      nowDate: new Date(),
      searchInput: '' as string,
      suppressDateRangeWatch: false as boolean,
      isEmptyKeywordSearch: false as boolean,
    }
  },
  computed: {
    filteredResults(): Tactic[] {
      return this.allSearchResultTactics.filter((tactic) => !(this as any).isTacticInFilteredOutPlan(tactic))
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    isNestedPlan(): boolean {
      return this.$store.getters.currentPlan.parentId.intID !== 0 ? true : false
    },
  },
  watch: {
    '$store.getters.currentDateRange': {
      deep: true,
      handler() {
        if (!this.suppressDateRangeWatch) {
          this.performSearch()
        }
      },
    },
  },
  mounted: function () {
    const startDate = new Date()
    startDate.setFullYear(startDate.getFullYear() - 1)
    const endDate = new Date()
    endDate.setFullYear(endDate.getFullYear() + 1)

    this.suppressDateRangeWatch = true
    this.$store.dispatch('updateCurrentDateRange', {
      newDateRange: [startDate, endDate],
      shouldFetchTactics: false,
    })
    this.$nextTick(() => {
      this.suppressDateRangeWatch = false
    })

    this.performSearch()
  },
  methods: {
    performSearch: function () {
      if (!this.searchQuery || this.searchQuery === '') return

      const dateRange = this.$store.getters.currentDateRange as Date[]

      this.isLoadingResults = true
      this.$store.dispatch('searchTactics', {
        keyword: this.searchQuery,
        startDate: dateRange[0],
        endDate: dateRange[1],
      }).then(
        (tactics: Tactic[]) => {
          this.allSearchResultTactics = tactics
          this.searchInput = this.searchQuery ?? ''
          this.isLoadingResults = false
        },
        (error: Error) => {
          this.isLoadingResults = false
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: error,
            life: 3000,
          })
        }
      )
    },
    showTactic: function (tactic: Tactic) {
      const thisTacticIntID = tactic.originalTacticID ? tactic.originalTacticID.intID : tactic.id.intID
      this.$router.push({ path: 'tactic/' + thisTacticIntID })
    },
    submitSearchInput: function () {
      if (!this.searchInput || this.searchInput === '') {
        this.isEmptyKeywordSearch = true
        return
      }
      this.isEmptyKeywordSearch = false
      this.$router.push({
        path: `/account/${this.$store.getters.currentAccount.id.intID}/plan/${this.$store.getters.currentPlan.id.intID}/search`,
        query: { q: this.searchInput },
      })
    },
    getPlatformNameForTactic(tactic: Tactic): string {
      return this.$store.getters.platformNamesForIds(tactic.tacticPlatformIds)
    },
    getTitleForTactic(tactic: Tactic) {
      let returnString = tactic.title
      if (this.isLeadPlan && tactic.abbreviatedPlanName !== '') {
        if (returnString.length > 0) {
          returnString = tactic.abbreviatedPlanName + '  |  ' + returnString
        } else {
          returnString = tactic.abbreviatedPlanName
        }
      }
      return returnString
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.search-view {
  .p-buttonset {
    .p-button {
      border-color: #efefef;
      background-color: #efefef;
      color: #333333;

      &:disabled {
        border-color: #000000;
        background-color: #000000;
        color: #ffffff;
        opacity: 1;
      }
    }
  }

  .p-datatable-wrapper span.is-lead {
    display: flex;
    column-gap: 1rem;

    &::before {
      content: '\e925'; // pi-home
      display: block;
      font-family: 'primeicons';
      font-size: 1.6rem;
      font-style: normal;
      font-weight: normal;
      font-variant: normal;
      text-transform: none;
    }
  }
}
</style>